import { useEffect, useMemo, useRef, useState } from "react";

import { filterOptions, type SmartOption } from "./smartMatch";

/**
 * Server search of a smart field (large entities: items, parties, accounts): the matches of `text`, which may be empty
 * (the first page). Keep the function stable (`useCallback`); a newer search aborts the older one.
 */
export type SmartSearch = (text: string, signal: AbortSignal) => Promise<readonly SmartOption[]>;

/** Rows a list renders at most; the rest are reached by typing (a hint says how many there are). */
export const defaultMaxShown = 100;

/** Wait after the last keystroke before a server search. */
const debounceMs = 250;

export interface SmartOptionsState {
  matches: readonly SmartOption[];
  total: number;
  busy: boolean;
  /** Options seen so far (given or found), by value: labels of chosen values after a server search. */
  known: ReadonlyMap<string, SmartOption>;
  /** Every local match of the query (not capped), for "select all". */
  allMatches: () => readonly SmartOption[];
}

/** The rows a smart field lists for `query`: its own options filtered locally, or the server's matches. */
export function useSmartOptions(
  options: readonly SmartOption[],
  query: string,
  open: boolean,
  search: SmartSearch | undefined,
  maxShown: number,
): SmartOptionsState {
  const [found, setFound] = useState<readonly SmartOption[] | null>(null);
  const [busy, setBusy] = useState(false);
  const known = useRef(new Map<string, SmartOption>());
  for (const option of options) known.current.set(option.value, option);

  useEffect(() => {
    if (!search || !open) return;
    const controller = new AbortController();
    const timer = window.setTimeout(
      () => {
        setBusy(true);
        search(query.trim(), controller.signal)
          .then((result) => {
            if (controller.signal.aborted) return;
            for (const option of result) known.current.set(option.value, option);
            setFound(result);
          })
          .catch(() => {
            if (!controller.signal.aborted) setFound([]);
          })
          .finally(() => {
            if (!controller.signal.aborted) setBusy(false);
          });
      },
      query.trim() === "" ? 0 : debounceMs,
    );
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [search, open, query]);

  const local = useMemo(() => filterOptions(options, query, maxShown), [options, query, maxShown]);
  // The server decides what matches; the given options stand in until its first answer.
  const rows = found ?? local.matches;
  const shown = useMemo(() => rows.slice(0, maxShown), [rows, maxShown]);
  if (!search)
    return { ...local, busy: false, known: known.current, allMatches: () => filterOptions(options, query).matches };
  return { matches: shown, total: rows.length, busy, known: known.current, allMatches: () => rows };
}
